import type { GqlFolderReportStatus } from "@/graphql/schema/types";

import classNames from "classnames";
import { Badge, Col, Dropdown, Row } from "react-bootstrap";
import { ChevronDown } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";

import Button from "@/components/ui/buttons/button";
import Loading from "@/components/ui/spinner/Loading";

import styles from "./FolderDownloadTopBanner.module.scss";

export interface IFolderDownloadItem {
  folderId: number;
  folderName: string;
  downloadUrl: string;
  status?: GqlFolderReportStatus | null;
  extension?: string | null;
  size?: number | null;
}

const STATUS_BADGE_META: Record<
  string,
  { badgeClass: string; labelKey: string }
> = {
  COMPLETED: {
    badgeClass: "bg-success-subtle text-success",
    labelKey: "ready",
  },
  PENDING: {
    badgeClass: "bg-primary-subtle text-primary",
    labelKey: "preparing",
  },
  FAILED: { badgeClass: "bg-danger-subtle text-danger", labelKey: "failed" },
};

function getStatusBadgeMeta(status?: string | null) {
  return STATUS_BADGE_META[status ?? ""] ?? STATUS_BADGE_META.PENDING;
}

interface IFolderDownloadsDropdownProps {
  folders: IFolderDownloadItem[];
  isOpen: boolean;
  onToggle: (nextIsOpen: boolean) => void;
  isRefreshing: boolean;
  isRetryingDownload: boolean;
  onDownload: (folder: IFolderDownloadItem) => void;
  onTryAgain: (folder: IFolderDownloadItem) => void;
}

function FolderDownloadsDropdown({
  folders,
  isOpen,
  onToggle,
  isRefreshing,
  isRetryingDownload,
  onDownload,
  onTryAgain,
}: IFolderDownloadsDropdownProps) {
  const { t } = useTranslation();

  const readyCount = folders.filter(
    (folder) => folder.status === "COMPLETED"
  ).length;

  return (
    <Dropdown show={isOpen} onToggle={onToggle} align="start">
      <Dropdown.Toggle
        variant="outline-primary"
        size="sm"
        bsPrefix="dropdown-toggle-no-caret"
        className={classNames(
          "rounded d-flex align-items-center gap-2 px-3 text-nowrap",
          styles.viewDownloadsToggle
        )}
      >
        {t("components.folderDownloadTopBanner.viewDownloads")} ({readyCount}/
        {folders.length})
        <ChevronDown
          size={16}
          className={classNames(styles.chevron, {
            [styles.chevronOpen]: isOpen,
          })}
        />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className={classNames(
          "shadow overflow-y-auto p-3 rounded-3 border-0",
          styles.folderDropdownMenu
        )}
        popperConfig={{
          modifiers: [{ name: "offset", options: { offset: [0, 28] } }],
        }}
      >
        <div className="fw-bold fs-5">
          {t("components.folderDownloadTopBanner.downloads")}
        </div>

        <div className="position-relative">
          {isRefreshing && (
            <>
              <Loading className="position-fixed start-50 top-50" />
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-body opacity-75"
                style={{ zIndex: 1 }}
              />
            </>
          )}

          <div className="px-lg-2">
            {folders.map((folder, index) => (
              <div key={folder.folderId}>
                <Row className="align-items-center py-3 gx-3">
                  <Col xs={7} className="d-flex align-items-center gap-2">
                    <span className="fw-bold text-truncate">
                      {folder.folderName}
                    </span>
                    <Badge
                      className={classNames(
                        "fw-normal fs-8 flex-shrink-0",
                        getStatusBadgeMeta(folder.status).badgeClass
                      )}
                    >
                      {t(
                        `components.folderDownloadTopBanner.status.${getStatusBadgeMeta(folder.status).labelKey}`
                      )}
                    </Badge>
                  </Col>

                  <Col xs={5}>
                    {folder.status === "FAILED" ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="w-100 rounded-3"
                        isLoading={isRetryingDownload}
                        onClick={() => onTryAgain(folder)}
                      >
                        {t("components.folderDownloadTopBanner.tryAgain")}
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-100 rounded-3 fw-bold"
                        disabled={folder.status !== "COMPLETED"}
                        onClick={() => onDownload(folder)}
                      >
                        {t("components.folderDownloadTopBanner.downloadFile")}
                      </Button>
                    )}
                  </Col>
                </Row>
                {index < folders.length - 1 && <hr className="m-0 p-0" />}
              </div>
            ))}
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default FolderDownloadsDropdown;
